import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useCruises } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useForm, useFieldArray } from 'react-hook-form';
import { motion } from 'framer-motion';
import { User, Mail, Lock, ChevronLeft, Loader2, CheckCircle, Award, PlusCircle, X, Star, Trash2, AlertTriangle, Download, Upload, FileText, Database, Smartphone, Wrench, Settings, UserPlus, Pencil, Info, ShieldCheck, Gem, History, Sparkles, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SHOW_DATA_MANAGEMENT } from '../config';
import { getLookupByEmail, updateFriendUsername, validateUsernameFormat, isUsernameDerogatory } from '../services/friendLookupService';
import { generateCsv, parseCsv, generateCsvTemplate } from '../utils/csvUtils';
import { isCarnivalCruiseLine, CARNIVAL_PLATINUM_BOOST_STARS } from '../utils/loyaltyUtils';
import toast from 'react-hot-toast';
import PasswordInput from '../components/PasswordInput';
import { Capacitor } from '@capacitor/core';
import {
  UserDisplayPreferences,
  DATE_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  UNIT_SYSTEM_OPTIONS,
  WEEK_START_OPTIONS,
  SUPPORTED_CURRENCIES,
} from '../types/userPreferences';

interface NameFormData {
  displayName: string;
  preferredDisplayName: string;
}

interface EmailFormData {
  newEmail: string;
  currentPassword?: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface LoyaltyFormData {
  loyaltyPrograms: {
    cruiseLineName: string;
    status: number;
    loyaltyNumber?: string;
    grandfatheredTier?: string;
    grandfatheredExpiry?: string;
    legacyPoints?: number;
    cycleEndDate?: string;
  }[];
}

interface DeleteAccountFormData {
  currentPassword: string;
}

interface DisplayPreferencesFormData {
  timeFormat: UserDisplayPreferences['timeFormat'];
  dateFormat: UserDisplayPreferences['dateFormat'];
  currency: string;
  units: UserDisplayPreferences['units'];
  weekStartsOn: UserDisplayPreferences['weekStartsOn'];
}
const UserProfile = () => {
  const { currentUser, activeSignInProvider, updateUserDisplayName, updateUserEmail, updateUserPassword, updateUserPreferredDisplayName, updateUserLoyaltyStatus, updateUserDisplayPreferences, deleteUserAccount, preferredDisplayName, loyaltyStatus, displayPreferences, linkGoogleAccount, linkAppleAccount, unlinkAuthProvider, logout } = useAuth();
  const isEmailPasswordSession = activeSignInProvider === 'password';
  const hasPasswordProvider = currentUser?.providerData?.some(p => p.providerId === 'password') ?? false;
  const { getAllCruisesForExport, bulkAddCruises, cruises } = useCruises();
  const { getLoyaltyCruiseLines, getLoyaltyTiersForCruiseLine, getLoyaltyTierName, getLoyaltyCalculationRules, getSecondaryTrackTier } = useCruiseMetadata();
  const navigate = useNavigate();
  const [isLoadingName, setIsLoadingName] = useState(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [isLoadingLoyalty, setIsLoadingLoyalty] = useState(false);
  const [isLoadingDisplayPreferences, setIsLoadingDisplayPreferences] = useState(false);
  const [isLinking, setIsLinking] = useState<'google' | 'apple' | null>(null);
  const [linkingConflictProvider, setLinkingConflictProvider] = useState<'Google' | 'Apple' | null>(null);




  const handleLinkGoogle = async () => {
    try {
      setIsLinking('google');
      await linkGoogleAccount();
    } catch (error: any) {
      if (error?.code === 'auth/credential-already-in-use' || error?.message?.includes('credential-already-in-use')) {
        setLinkingConflictProvider('Google');
      }
    } finally {
      setIsLinking(null);
    }
  };

  const handleLinkApple = async () => {
    try {
      setIsLinking('apple');
      await linkAppleAccount();
    } catch (error: any) {
      if (error?.code === 'auth/credential-already-in-use' || error?.message?.includes('credential-already-in-use')) {
        setLinkingConflictProvider('Apple');
      }
    } finally {
      setIsLinking(null);
    }
  };

  const handleUnlinkProvider = async (providerId: string) => {
    try {
      setIsLinking(providerId === 'google.com' ? 'google' : 'apple');
      await unlinkAuthProvider(providerId);
    } catch {
      // Error toast handled in context
    } finally {
      setIsLinking(null);
    }
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [friendUsername, setFriendUsernameState] = useState<string | null>(null);
  const [loadingFriendUsername, setLoadingFriendUsername] = useState(true);
  const [showEditFriendUsernameModal, setShowEditFriendUsernameModal] = useState(false);
  const [editFriendUsernameValue, setEditFriendUsernameValue] = useState('');
  const [isLoadingEditFriendUsername, setIsLoadingEditFriendUsername] = useState(false);
  const [editFriendUsernameError, setEditFriendUsernameError] = useState('');
  const [editingLoyaltyIndex, setEditingLoyaltyIndex] = useState<number | null>(null);
  const [isAddingLoyaltyProgram, setIsAddingLoyaltyProgram] = useState(false);

  const nameForm = useForm<NameFormData>({
    defaultValues: {
      displayName: currentUser?.displayName || '',
      preferredDisplayName: preferredDisplayName || ''
    }
  });

  const emailForm = useForm<EmailFormData>({
    defaultValues: {
      newEmail: currentUser?.email || '',
      currentPassword: ''
    }
  });

  const passwordForm = useForm<PasswordFormData>();

  const loyaltyForm = useForm<LoyaltyFormData>({
    defaultValues: {
      loyaltyPrograms: loyaltyStatus.length > 0 ? loyaltyStatus : []
    }
  });

  const { fields: loyaltyFields, append: appendLoyalty, remove: removeLoyalty } = useFieldArray({
    control: loyaltyForm.control,
    name: "loyaltyPrograms"
  });

  const deleteForm = useForm<DeleteAccountFormData>();

  const displayPreferencesForm = useForm<DisplayPreferencesFormData>({
    defaultValues: {
      timeFormat: displayPreferences.timeFormat,
      dateFormat: displayPreferences.dateFormat,
      currency: displayPreferences.currency,
      units: displayPreferences.units,
      weekStartsOn: displayPreferences.weekStartsOn,
    }
  });

  useEffect(() => {
    if (loyaltyStatus && loyaltyStatus.length > 0 && editingLoyaltyIndex === null && !isAddingLoyaltyProgram) {
      const normalized = loyaltyStatus.map((prog) => {
        const rules = getLoyaltyCalculationRules(prog.cruiseLineName);
        const isStars = rules?.method === 'manual' || rules?.supportsGrandfathering;
        if (isCarnivalCruiseLine(prog.cruiseLineName) && prog.grandfatheredTier === 'Platinum' && (isStars || Number(prog.status) < CARNIVAL_PLATINUM_BOOST_STARS)) {
          return {
            ...prog,
            status: Math.max(CARNIVAL_PLATINUM_BOOST_STARS, Number(prog.status) || 0),
          };
        }
        return prog;
      });
      loyaltyForm.reset({
        loyaltyPrograms: normalized
      });
    }
  }, [loyaltyStatus, loyaltyForm, editingLoyaltyIndex, isAddingLoyaltyProgram, getLoyaltyCalculationRules]);

  useEffect(() => {
    nameForm.reset({
      displayName: currentUser?.displayName || '',
      preferredDisplayName: preferredDisplayName || ''
    });
  }, [currentUser?.displayName, preferredDisplayName, nameForm]);

  useEffect(() => {
    displayPreferencesForm.reset({
      timeFormat: displayPreferences.timeFormat,
      dateFormat: displayPreferences.dateFormat,
      currency: displayPreferences.currency,
      units: displayPreferences.units,
      weekStartsOn: displayPreferences.weekStartsOn,
    });
  }, [displayPreferences, displayPreferencesForm]);

  useEffect(() => {
    if (!currentUser?.email) {
      setLoadingFriendUsername(false);
      return;
    }
    let cancelled = false;
    getLookupByEmail(currentUser.email).then((lookup) => {
      if (!cancelled) {
        setFriendUsernameState(lookup?.username ?? null);
        setLoadingFriendUsername(false);
      }
    }).catch(() => {
      if (!cancelled) setLoadingFriendUsername(false);
    });
    return () => { cancelled = true; };
  }, [currentUser?.email]);

  const handleOpenEditFriendUsername = () => {
    setEditFriendUsernameValue(friendUsername ?? '');
    setEditFriendUsernameError('');
    setShowEditFriendUsernameModal(true);
  };

  const handleSaveFriendUsername = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.email) return;
    setEditFriendUsernameError('');
    const trimmed = editFriendUsernameValue.trim().replace(/^@+/, '');
    if (!trimmed) {
      setEditFriendUsernameError('Please enter a username');
      return;
    }
    const formatCheck = validateUsernameFormat(trimmed);
    if (!formatCheck.valid) {
      setEditFriendUsernameError(formatCheck.error ?? 'Invalid username');
      return;
    }
    if (isUsernameDerogatory(trimmed)) {
      setEditFriendUsernameError('This username is not allowed. Please choose another.');
      return;
    }
    setIsLoadingEditFriendUsername(true);
    try {
      await updateFriendUsername(currentUser.uid, currentUser.email, trimmed, friendUsername);
      setFriendUsernameState(trimmed.toLowerCase());
      setShowEditFriendUsernameModal(false);
      toast.success('Friend username updated');
    } catch (err: any) {
      setEditFriendUsernameError(err.message ?? 'Failed to update username');
    } finally {
      setIsLoadingEditFriendUsername(false);
    }
  };

  const handleExportCruises = async () => {
    try {
      setIsExporting(true);
      const cruises = await getAllCruisesForExport();
      
      if (cruises.length === 0) {
        toast.error('No cruises to export');
        return;
      }
      
      const csvContent = generateCsv(cruises);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-cruises-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Successfully exported ${cruises.length} cruise${cruises.length !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error('Error exporting cruises:', error);
      toast.error('Failed to export cruises. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const templateContent = generateCsvTemplate();
    const blob = new Blob([templateContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cruise-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Template downloaded! Fill it out and import your cruises.');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImportCruises = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file to import');
      return;
    }
    
    try {
      setIsImporting(true);
      
      // Read file content
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(selectedFile);
      });
      
      // Parse CSV
      const parsedCruises = parseCsv(fileContent);
      
      if (parsedCruises.length === 0) {
        toast.error('No valid cruise data found in the file');
        return;
      }
      
      // Show preview modal
      setImportPreview(parsedCruises);
      setShowImportModal(true);
      
    } catch (error: any) {
      console.error('Error parsing CSV:', error);
      toast.error(error.message || 'Failed to parse CSV file. Please check the format.');
    } finally {
      setIsImporting(false);
    }
  };

  const confirmImport = async () => {
    try {
      setIsImporting(true);
      await bulkAddCruises(importPreview);
      
      // Reset state
      setShowImportModal(false);
      setImportPreview([]);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Error importing cruises:', error);
      toast.error('Failed to import cruises. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const cancelImport = () => {
    setShowImportModal(false);
    setImportPreview([]);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmitName = async (data: NameFormData) => {
    try {
      setIsLoadingName(true);
      
      // Update both display name and preferred display name (suppress individual toasts)
      await updateUserDisplayName(data.displayName, true);
      await updateUserPreferredDisplayName(data.preferredDisplayName || null, true);
      
      // Show single consolidated success message
      toast.success('Name updated successfully');
    } catch {
      toast.error('Failed to update name');
    } finally {
      setIsLoadingName(false);
    }
  };

  const onSubmitEmail = async (data: EmailFormData) => {
    try {
      setIsLoadingEmail(true);
      await updateUserEmail(data.newEmail, data.currentPassword);
      emailForm.reset();
    } catch {
      // Error toast handled inside updateUserEmail
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    try {
      setIsLoadingPassword(true);
      await updateUserPassword(data.currentPassword, data.newPassword);
      passwordForm.reset();
    } catch {
      // Error toast handled inside updateUserPassword
    } finally {
      setIsLoadingPassword(false);
    }
  };


  const onSubmitDisplayPreferences = async (data: DisplayPreferencesFormData) => {
    try {
      setIsLoadingDisplayPreferences(true);
      await updateUserDisplayPreferences(data);
    } finally {
      setIsLoadingDisplayPreferences(false);
    }
  };

  const onSubmitDelete = async (data: DeleteAccountFormData) => {
    try {
      setIsLoadingDelete(true);
      await deleteUserAccount(data.currentPassword);
      setShowDeleteModal(false);
      navigate('/signup', { replace: true });
    } catch {
      // Deletion was cancelled or failed; close delete modal and remain on UserProfile
      setShowDeleteModal(false);
    } finally {
      setIsLoadingDelete(false);
    }
  };
  const addLoyaltyProgram = () => {
    if (isAddingLoyaltyProgram || editingLoyaltyIndex !== null) {
      toast('Please finish saving or canceling your current edit before adding another program.', { icon: <Info className="h-5 w-5 text-secondary-500" /> });
      return;
    }
    appendLoyalty({ cruiseLineName: '', status: 0, loyaltyNumber: '', grandfatheredTier: '', legacyPoints: undefined });
    setIsAddingLoyaltyProgram(true);
    setEditingLoyaltyIndex(loyaltyFields.length);
  };

  const handleSaveLoyaltyProgram = async () => {
    const isValid = await loyaltyForm.trigger();
    if (!isValid) return;
    const data = loyaltyForm.getValues();
    try {
      setIsLoadingLoyalty(true);
      // Enforce 10,000 Star biennial bonus for Grandfathered Platinum Carnival accounts
      const sanitizedPrograms = data.loyaltyPrograms.map((p) => {
        const rules = getLoyaltyCalculationRules(p.cruiseLineName);
        const isStars = rules?.method === 'manual' || rules?.supportsGrandfathering;
        if (isCarnivalCruiseLine(p.cruiseLineName) && p.grandfatheredTier === 'Platinum') {
          const numStatus = Number(p.status) || 0;
          return {
            ...p,
            status: isStars || numStatus < CARNIVAL_PLATINUM_BOOST_STARS ? Math.max(CARNIVAL_PLATINUM_BOOST_STARS, numStatus) : numStatus,
            grandfatheredExpiry: p.grandfatheredExpiry || '2028-05-31',
            cycleEndDate: p.cycleEndDate || '2028-12-31',
          };
        }
        return p;
      });
      await updateUserLoyaltyStatus(sanitizedPrograms);
      setEditingLoyaltyIndex(null);
      setIsAddingLoyaltyProgram(false);
      toast.success('Loyalty programs saved');
    } catch {
      toast.error('Failed to save loyalty program');
    } finally {
      setIsLoadingLoyalty(false);
    }
  };

  const handleCancelLoyaltyEdit = (index: number) => {
    const program = loyaltyForm.getValues(`loyaltyPrograms.${index}`);
    if (!program?.cruiseLineName || isAddingLoyaltyProgram) {
      removeLoyalty(index);
      setIsAddingLoyaltyProgram(false);
    } else if (loyaltyStatus[index]) {
      const rec = loyaltyStatus[index];
      const rules = getLoyaltyCalculationRules(rec.cruiseLineName);
      const isStars = rules?.method === 'manual' || rules?.supportsGrandfathering;
      let statusVal = rec.status;
      if (isCarnivalCruiseLine(rec.cruiseLineName) && rec.grandfatheredTier === 'Platinum' && (isStars || statusVal < CARNIVAL_PLATINUM_BOOST_STARS)) {
        statusVal = Math.max(CARNIVAL_PLATINUM_BOOST_STARS, statusVal);
      }
      loyaltyForm.setValue(`loyaltyPrograms.${index}.status`, statusVal);
      loyaltyForm.setValue(`loyaltyPrograms.${index}.loyaltyNumber`, rec.loyaltyNumber || '');
      loyaltyForm.setValue(`loyaltyPrograms.${index}.grandfatheredTier`, rec.grandfatheredTier || '');
      loyaltyForm.setValue(`loyaltyPrograms.${index}.grandfatheredExpiry`, rec.grandfatheredExpiry || '');
      loyaltyForm.setValue(`loyaltyPrograms.${index}.legacyPoints`, rec.legacyPoints);
      loyaltyForm.setValue(`loyaltyPrograms.${index}.cycleEndDate`, rec.cycleEndDate || '');
    }
    setEditingLoyaltyIndex(null);
  };

  const handleRemoveLoyaltyProgram = async (index: number) => {
    const programName = loyaltyForm.getValues(`loyaltyPrograms.${index}.cruiseLineName`) || 'this program';
    if (!window.confirm(`Are you sure you want to remove ${programName}?`)) return;

    const currentPrograms = loyaltyForm.getValues('loyaltyPrograms');
    const updatedPrograms = currentPrograms.filter((_, i) => i !== index);
    removeLoyalty(index);
    try {
      setIsLoadingLoyalty(true);
      await updateUserLoyaltyStatus(updatedPrograms);
      toast.success('Loyalty program removed');
    } catch {
      toast.error('Failed to remove loyalty program');
    } finally {
      setIsLoadingLoyalty(false);
      setEditingLoyaltyIndex(null);
      setIsAddingLoyaltyProgram(false);
    }
  };

  const loyaltyCruiseLines = getLoyaltyCruiseLines();
  const usedCruiseLines = loyaltyForm.watch('loyaltyPrograms').map(program => program.cruiseLineName);

  const getLoyaltyUnitForCruiseLine = (cruiseLine: string): string => {
    const loyaltyData = getLoyaltyTiersForCruiseLine(cruiseLine);
    return loyaltyData?.unit || 'points';
  };

  const formatCycleDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        }
      }
    } catch {
      // fallback
    }
    return dateStr;
  };

  const renderLoyaltyTierInfo = (cruiseLine: string, status: number, grandfatheredTier?: string) => {
    if (!cruiseLine || status === undefined) return null;
    
    const tierName = getLoyaltyTierName(cruiseLine, status, grandfatheredTier);
    const loyaltyData = getLoyaltyTiersForCruiseLine(cruiseLine);
    const programName = loyaltyData?.programName;
    const rules = getLoyaltyCalculationRules(cruiseLine);

    // Secondary track: count completed in-app sailings for this cruise line
    const secondarySailingCount = rules?.secondaryTrack
      ? cruises.filter(c => c.cruiseLine === cruiseLine && c.completed).length
      : undefined;
    const secondaryTierName = rules?.secondaryTrack && secondarySailingCount !== undefined
      ? getSecondaryTrackTier(cruiseLine, secondarySailingCount)
      : null;

    // Determine which track wins (compare min_value thresholds)
    const nightsTierMin = rules?.secondaryTrack && tierName
      ? (loyaltyData?.tiers.find(t => t.name === tierName)?.min_value ?? 0)
      : 0;
    const secondaryTierMin = secondaryTierName
      ? (rules?.secondaryTiers?.find(t => t.name === secondaryTierName)?.min_value ?? 0)
      : 0;
    const secondaryWins = secondaryTierName !== null && secondaryTierMin > nightsTierMin;
    const effectiveTier = secondaryWins ? secondaryTierName : tierName;
    
    if (!effectiveTier && !programName) return null;

    return (
      <div className="mt-2 space-y-1">
        {programName && (
          <div className="flex items-center">
            <span className="text-sm text-gray-600">
              Program Name: <span className="font-medium text-gray-700">{programName}</span>
            </span>
          </div>
        )}
        {effectiveTier && (
          <div className="flex items-center">
            <Star className="h-4 w-4 text-yellow-500 mr-1" />
            <span className="text-sm font-medium text-gray-700">
              Current Tier: <span className="text-yellow-600">{effectiveTier}</span>
            </span>
          </div>
        )}
        {/* Secondary track display for programs like Princess Captain's Circle */}
        {secondarySailingCount !== undefined && (
          <div className="mt-1 text-xs text-gray-500 bg-gray-100 rounded px-2 py-1 inline-flex items-center gap-2">
            <span>{status.toLocaleString()} {loyaltyData?.unit || 'nights'}</span>
            <span className="text-gray-400">·</span>
            <span>{secondarySailingCount} sailing{secondarySailingCount !== 1 ? 's' : ''} (in-app)</span>
            {secondaryWins && (
              <span className="text-yellow-600 font-medium">cruise count wins</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div 
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="mb-6 flex items-center">
        <button 
          onClick={() => navigate(-1)}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Profile Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account preferences</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Name Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center mb-4">
            <User className="h-5 w-5 text-secondary-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Name</h2>
          </div>

          <form onSubmit={nameForm.handleSubmit(onSubmitName)} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <p className="text-sm text-gray-500 mb-2">
                This is the name associated with your account
              </p>
              <input
                id="displayName"
                type="text"
                {...nameForm.register('displayName', { required: 'Full name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
              {nameForm.formState.errors.displayName && (
                <p className="mt-1 text-sm text-red-600">
                  {nameForm.formState.errors.displayName.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="preferredDisplayName" className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Optional: Choose how you'd like your name to be displayed
              </p>
              <input
                id="preferredDisplayName"
                type="text"
                {...nameForm.register('preferredDisplayName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Leave blank to use your full name"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoadingName}
                className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                {isLoadingName ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Updating name...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Consolidated Friends & Discovery Settings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-lg shadow-md p-6 space-y-6"
        >
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <div className="flex items-center">
              <UserPlus className="h-5 w-5 text-secondary-500 mr-2" />
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Friends & Discovery Settings</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Manage your `@username` so friends can search for and connect with you on Cruising Along.
                </p>
              </div>
            </div>
          </div>

          {currentUser?.email?.toLowerCase().includes('privaterelay.appleid.com') && (
            <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-950">Apple Private Relay Account</p>
                <p className="mt-0.5 text-blue-800">
                  Because you chose to hide your email during Sign in with Apple, friends connect with you using your unique <strong>Friend Username</strong> below.
                </p>
              </div>
            </div>
          )}

          {/* Friend Username Display / Edit */}
          {!loadingFriendUsername && (
            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700">
                Friend Username
              </span>
              {friendUsername ? (
                <div className="flex items-center justify-between flex-wrap gap-3 bg-gray-50 p-3.5 rounded-md border border-gray-200">
                  <div>
                    <span className="text-secondary-700 font-semibold text-base">@{friendUsername}</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Friends can search for you using this `@username`.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenEditFriendUsername}
                    className="flex items-center px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-white transition-colors font-medium text-gray-700 shadow-sm"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1 text-gray-500" />
                    Edit Username
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between flex-wrap gap-3 bg-amber-50 p-3.5 rounded-md border border-amber-200">
                  <div>
                    <span className="text-amber-900 font-semibold text-sm">No Username Set</span>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Set a friend username so others can easily find and connect with you!
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenEditFriendUsername}
                    className="flex items-center px-3 py-1.5 text-xs bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium shadow-sm"
                  >
                    <UserPlus className="h-3.5 w-3.5 mr-1" />
                    Set Username
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Primary Login Email Card (Displayed if account has Email/Password credentials configured) */}
        {hasPasswordProvider && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0" />
                <h2 className="text-xl font-semibold text-gray-800">Login Email (Authentication)</h2>
              </div>
              {!isEmailPasswordSession && (
                <span className="inline-flex items-center justify-center text-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 self-start sm:self-auto">
                  Signed in via {activeSignInProvider === 'google.com' ? 'Google' : 'Apple'}
                </span>
              )}
            </div>

            {isEmailPasswordSession ? (
              <form onSubmit={emailForm.handleSubmit(onSubmitEmail)} className="space-y-4">
                <div>
                  <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    New Login Email Address
                  </label>
                  <input
                    id="newEmail"
                    type="email"
                    {...emailForm.register('newEmail', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                  />
                  {emailForm.formState.errors.newEmail && (
                    <p className="mt-1 text-sm text-red-600">
                      {emailForm.formState.errors.newEmail.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="emailCurrentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <PasswordInput
                    id="emailCurrentPassword"
                    register={emailForm.register('currentPassword', { required: 'Current password is required' })}
                    error={!!emailForm.formState.errors.currentPassword}
                  />
                  {emailForm.formState.errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {emailForm.formState.errors.currentPassword.message}
                    </p>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  Note: Updating your email address will send a verification link to the new address and sign you out. You must click the verification link before logging back in.
                </p>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoadingEmail}
                    className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                  >
                    {isLoadingEmail ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Updating email...
                      </>
                    ) : (
                      <>
                        <Mail className="h-5 w-5 mr-2" />
                        Update Login Email
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Login Email Address
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 font-medium text-sm">
                    {currentUser?.email || 'No email on file'}
                  </div>
                </div>

                <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900">
                  <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-blue-950">Signed in via {activeSignInProvider === 'google.com' ? 'Google' : 'Apple'}</p>
                    <p className="mt-0.5 text-blue-800">
                      Your account has email and password credentials, but you are currently signed in through {activeSignInProvider === 'google.com' ? 'Google' : 'Apple'}. To change your login email address, please log out and sign in using your email and password.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Password Section (Rendered only when logged in via email/password credentials) */}
        {hasPasswordProvider && isEmailPasswordSession && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex items-center mb-4">
              <Lock className="h-5 w-5 text-secondary-500 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Change Password</h2>
            </div>

            <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <PasswordInput
                  id="currentPassword"
                  register={passwordForm.register('currentPassword', { required: 'Current password is required' })}
                  error={!!passwordForm.formState.errors.currentPassword}
                />
                {passwordForm.formState.errors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordForm.formState.errors.currentPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <PasswordInput
                  id="newPassword"
                  register={passwordForm.register('newPassword', {
                    required: 'New password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  error={!!passwordForm.formState.errors.newPassword}
                />
                {passwordForm.formState.errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordForm.formState.errors.newPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <PasswordInput
                  id="confirmPassword"
                  register={passwordForm.register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => value === passwordForm.watch('newPassword') || 'Passwords do not match'
                  })}
                  error={!!passwordForm.formState.errors.confirmPassword}
                />
                {passwordForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {passwordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoadingPassword}
                  className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                >
                  {isLoadingPassword ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Updating password...
                    </>
                  ) : (
                    <>
                      <Lock className="h-5 w-5 mr-2" />
                      Update Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Connected Accounts Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center mb-4">
            <Lock className="h-5 w-5 text-secondary-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Connected Accounts</h2>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Link your Google or Apple accounts to log in with one click.
          </p>

          <div className="space-y-4">
            {/* Google Provider Row */}
            {(() => {
              const googleProvider = currentUser?.providerData.find(p => p.providerId === 'google.com');
              return (
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50/50 gap-3">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">Google Account</p>
                      <p
                        className="text-xs text-gray-500 truncate"
                        title={googleProvider?.email ? `Connected (${googleProvider.email})` : undefined}
                      >
                        {googleProvider
                          ? (googleProvider.email ? `Connected (${googleProvider.email})` : 'Connected')
                          : 'Not connected'}
                      </p>
                    </div>
                  </div>
                  {googleProvider ? (
                    <button
                      type="button"
                      onClick={() => handleUnlinkProvider('google.com')}
                      disabled={isLinking !== null}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      Unlink
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleLinkGoogle}
                      disabled={isLinking !== null}
                      className="flex-shrink-0 flex items-center px-3 py-1.5 text-xs font-medium text-secondary-600 border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors disabled:opacity-50"
                    >
                      {isLinking === 'google' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      Connect
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Apple Provider Row */}
            {(() => {
              const appleProvider = currentUser?.providerData.find(p => p.providerId === 'apple.com');
              return (
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50/50 gap-3">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <svg className="h-5 w-auto fill-current text-gray-900 flex-shrink-0" viewBox="0 0 384 512">
                      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/>
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">Apple Account</p>
                      <p
                        className="text-xs text-gray-500 truncate"
                        title={appleProvider?.email ? `Connected (${appleProvider.email})` : undefined}
                      >
                        {appleProvider
                          ? (appleProvider.email ? `Connected (${appleProvider.email})` : 'Connected')
                          : 'Not connected'}
                      </p>
                    </div>
                  </div>
                  {appleProvider ? (
                    <button
                      type="button"
                      onClick={() => handleUnlinkProvider('apple.com')}
                      disabled={isLinking !== null}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      Unlink
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleLinkApple}
                      disabled={isLinking !== null}
                      className="flex-shrink-0 flex items-center px-3 py-1.5 text-xs font-medium text-secondary-600 border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors disabled:opacity-50"
                    >
                      {isLinking === 'apple' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      Connect
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </motion.div>

        {/* Loyalty Programs Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Award className="h-5 w-5 text-secondary-500 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Loyalty Programs</h2>
            </div>
            <button
              type="button"
              onClick={addLoyaltyProgram}
              className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Add Program
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Track your loyalty status across different cruise lines. Enter your current status level or points to see your tier.
          </p>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            {loyaltyFields.length === 0 ? (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                <Award className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium text-gray-700">No loyalty programs added yet</p>
                <p className="text-sm text-gray-500 mt-1">Click "Add Program" to track your status & tier</p>
              </div>
            ) : (
              <div className="space-y-4">
                {loyaltyFields.map((field, index) => {
                  const selectedCruiseLine = loyaltyForm.watch(`loyaltyPrograms.${index}.cruiseLineName`);
                  const currentStatus = loyaltyForm.watch(`loyaltyPrograms.${index}.status`);
                  const currentLoyaltyNumber = loyaltyForm.watch(`loyaltyPrograms.${index}.loyaltyNumber`);
                  const currentGrandfatheredTier = loyaltyForm.watch(`loyaltyPrograms.${index}.grandfatheredTier`);
                  const currentLegacyPoints = loyaltyForm.watch(`loyaltyPrograms.${index}.legacyPoints`);
                  const loyaltyUnit = selectedCruiseLine ? getLoyaltyUnitForCruiseLine(selectedCruiseLine) : 'points';
                  const isEditingThisCard = editingLoyaltyIndex === index;
                  const isNewCard = isAddingLoyaltyProgram && index === loyaltyFields.length - 1 && !selectedCruiseLine;

                  const tierName = selectedCruiseLine && currentStatus !== undefined ? getLoyaltyTierName(selectedCruiseLine, currentStatus, currentGrandfatheredTier) : null;
                  const loyaltyData = selectedCruiseLine ? getLoyaltyTiersForCruiseLine(selectedCruiseLine) : null;
                  const programName = loyaltyData?.programName;
                  const rules = selectedCruiseLine ? getLoyaltyCalculationRules(selectedCruiseLine) : null;

                  const secondarySailingCount = rules?.secondaryTrack && selectedCruiseLine
                    ? cruises.filter(c => c.cruiseLine === selectedCruiseLine && c.completed).length
                    : undefined;
                  const secondaryTierName = rules?.secondaryTrack && selectedCruiseLine && secondarySailingCount !== undefined
                    ? getSecondaryTrackTier(selectedCruiseLine, secondarySailingCount)
                    : null;

                  const nightsTierMin = rules?.secondaryTrack && tierName
                    ? (loyaltyData?.tiers.find(t => t.name === tierName)?.min_value ?? 0)
                    : 0;
                  const secondaryTierMin = secondaryTierName
                    ? (rules?.secondaryTiers?.find(t => t.name === secondaryTierName)?.min_value ?? 0)
                    : 0;
                  const secondaryWins = secondaryTierName !== null && secondaryTierMin > nightsTierMin;
                  const effectiveTier = secondaryWins ? secondaryTierName : tierName;

                  // Render Editing or Adding Card
                  if (isEditingThisCard || isNewCard) {
                    return (
                      <div key={field.id} className={`p-5 rounded-xl transition-all shadow-sm space-y-4 ${
                        isNewCard 
                          ? 'border-2 border-dashed border-secondary-300 bg-secondary-50/10' 
                          : 'border-2 border-secondary-400 bg-secondary-50/20'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs uppercase font-semibold text-secondary-700 tracking-wider">
                            {isNewCard ? 'Add New Loyalty Program' : 'Editing Loyalty Program'}
                          </span>
                          {!isNewCard && (
                            <span className="inline-flex items-center justify-center text-center text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded border border-gray-200 shrink-0">
                              <Lock className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
                              Cruise Line Locked
                            </span>
                          )}
                        </div>

                        <div>
                          <label htmlFor={`loyalty-line-select-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                            Cruise Line
                          </label>
                          {isNewCard ? (
                            <select
                              id={`loyalty-line-select-${index}`}
                              {...loyaltyForm.register(`loyaltyPrograms.${index}.cruiseLineName`, {
                                required: 'Please select a cruise line'
                              })}
                              value={selectedCruiseLine || ''}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 text-gray-900 bg-white"
                            >
                              <option value="">Select cruise line</option>
                              {loyaltyCruiseLines
                                .filter(cl => !usedCruiseLines.includes(cl.name) || selectedCruiseLine === cl.name)
                                .map(cruiseLine => (
                                  <option key={cruiseLine.name} value={cruiseLine.name}>
                                    {cruiseLine.name}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-800 font-medium text-sm flex items-center justify-between">
                              <span className="font-semibold text-gray-900">{selectedCruiseLine}</span>
                              {programName && <span className="text-xs text-gray-500 font-normal">({programName})</span>}
                            </div>
                          )}
                          {loyaltyForm.formState.errors.loyaltyPrograms?.[index]?.cruiseLineName && (
                            <p className="mt-1 text-sm text-red-600">
                              {loyaltyForm.formState.errors.loyaltyPrograms[index]?.cruiseLineName?.message}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor={`loyalty-status-input-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                              Status ({loyaltyUnit})
                            </label>
                            <input
                              id={`loyalty-status-input-${index}`}
                              type="number"
                              min={isCarnivalCruiseLine(selectedCruiseLine) && currentGrandfatheredTier === 'Platinum' ? CARNIVAL_PLATINUM_BOOST_STARS : 0}
                              {...loyaltyForm.register(`loyaltyPrograms.${index}.status`, {
                                required: 'Please enter your status',
                                min: {
                                  value: isCarnivalCruiseLine(selectedCruiseLine) && currentGrandfatheredTier === 'Platinum' ? CARNIVAL_PLATINUM_BOOST_STARS : 0,
                                  message: isCarnivalCruiseLine(selectedCruiseLine) && currentGrandfatheredTier === 'Platinum'
                                    ? `Grandfathered Platinum requires at least ${CARNIVAL_PLATINUM_BOOST_STARS.toLocaleString()} Stars (includes the 10k biennial bonus)`
                                    : 'Status must be 0 or greater'
                                },
                                valueAsNumber: true
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 text-gray-900 bg-white"
                              placeholder={`Enter ${loyaltyUnit}`}
                            />
                            {isCarnivalCruiseLine(selectedCruiseLine) && currentGrandfatheredTier === 'Platinum' && (
                              <p className="mt-1.5 text-xs text-indigo-700 flex items-center gap-1 font-medium bg-indigo-50/80 border border-indigo-200/60 rounded px-2 py-1">
                                <Sparkles className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                                Includes 10,000 Star biennial bonus (minimum 10,000 Stars).
                              </p>
                            )}
                            {loyaltyForm.formState.errors.loyaltyPrograms?.[index]?.status && (
                              <p className="mt-1 text-sm text-red-600">
                                {loyaltyForm.formState.errors.loyaltyPrograms[index]?.status?.message}
                              </p>
                            )}
                          </div>

                          <div>
                            <label htmlFor={`loyalty-number-input-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                              Loyalty Number <span className="font-normal text-gray-500">(Optional)</span>
                            </label>
                            <input
                              id={`loyalty-number-input-${index}`}
                              type="text"
                              {...loyaltyForm.register(`loyaltyPrograms.${index}.loyaltyNumber`)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 text-gray-900 bg-white"
                              placeholder="e.g. 123456789"
                            />
                          </div>
                        </div>

                        {/* Grandfathered and Cycle Controls for Carnival / Grandfathered Programs */}
                        {(selectedCruiseLine?.toLowerCase().includes('carnival') || rules?.supportsGrandfathering) && (
                          <div className="p-3.5 bg-indigo-50/70 border border-indigo-200 rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                                <ShieldCheck className="h-4 w-4 text-indigo-600" />
                                Grandfathered Status & Cycle Settings
                              </span>
                              <span className="text-xs text-indigo-700 bg-indigo-100/60 px-2 py-0.5 rounded font-medium">Cycle: Sep 2026 – Dec 2028</span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label htmlFor={`loyalty-grand-tier-${index}`} className="block text-xs font-semibold text-indigo-900 mb-1">
                                  Grandfathered Tier
                                </label>
                                <select
                                  id={`loyalty-grand-tier-${index}`}
                                  {...loyaltyForm.register(`loyaltyPrograms.${index}.grandfatheredTier`, {
                                    onChange: (e) => {
                                      const chosen = e.target.value;
                                      if (chosen === 'Platinum') {
                                        const curStatus = Number(loyaltyForm.getValues(`loyaltyPrograms.${index}.status`)) || 0;
                                        if (curStatus < CARNIVAL_PLATINUM_BOOST_STARS) {
                                          loyaltyForm.setValue(`loyaltyPrograms.${index}.status`, CARNIVAL_PLATINUM_BOOST_STARS, { shouldDirty: true, shouldValidate: true });
                                          toast.success('Applied 10,000 Star biennial bonus for Grandfathered Platinum');
                                        }
                                        if (!loyaltyForm.getValues(`loyaltyPrograms.${index}.grandfatheredExpiry`)) {
                                          loyaltyForm.setValue(`loyaltyPrograms.${index}.grandfatheredExpiry`, '2028-05-31');
                                        }
                                      }
                                    }
                                  })}
                                  className="w-full px-2.5 py-1.5 text-xs border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 bg-white"
                                >
                                  <option value="">None (Standard Earning)</option>
                                  <option value="Diamond">Lifetime Diamond (Earned under VIFP)</option>
                                  <option value="Platinum">Grandfathered Platinum (Thru May 2028 + 10k Boost)</option>
                                  <option value="Gold">Grandfathered Gold</option>
                                  <option value="Red">Grandfathered Red</option>
                                </select>
                              </div>

                              <div>
                                <label htmlFor={`loyalty-legacy-points-${index}`} className="block text-xs font-semibold text-indigo-900 mb-1">
                                  Preserved Legacy VIFP Days
                                </label>
                                <input
                                  id={`loyalty-legacy-points-${index}`}
                                  type="number"
                                  min="0"
                                  {...loyaltyForm.register(`loyaltyPrograms.${index}.legacyPoints`, { valueAsNumber: true })}
                                  className="w-full px-2.5 py-1.5 text-xs border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 bg-white"
                                  placeholder="e.g. 85 days"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Live Tier Preview */}
                        {selectedCruiseLine && currentStatus !== undefined && (
                          <div className="pt-2 border-t border-gray-200">
                            {renderLoyaltyTierInfo(selectedCruiseLine, currentStatus, currentGrandfatheredTier)}
                          </div>
                        )}

                        <div className="flex justify-end space-x-2 pt-2">
                          <button
                            type="button"
                            onClick={() => handleCancelLoyaltyEdit(index)}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={isLoadingLoyalty}
                            onClick={handleSaveLoyaltyProgram}
                            className="flex items-center px-4 py-1.5 text-sm font-medium bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors shadow-sm"
                          >
                            {isLoadingLoyalty ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1.5" />
                            )}
                            {isNewCard ? 'Save Program' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    );
                  }

                  // Render Saved Read-Only Program Card
                  const currentProgramRecord = loyaltyStatus[index];
                  return (
                    <div key={field.id} className="p-5 border border-gray-200 rounded-xl bg-white shadow-sm transition-all hover:shadow-md">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-bold text-gray-900">{selectedCruiseLine}</h3>
                            {effectiveTier && (
                              <span className="inline-flex items-center justify-center text-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 shadow-sm shrink-0">
                                <Star className="h-3 w-3 mr-1 text-amber-500 fill-amber-500 flex-shrink-0" />
                                {effectiveTier}
                              </span>
                            )}
                            {currentProgramRecord?.grandfatheredTier && (
                              <span className="inline-flex items-center justify-center text-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-800 border border-purple-200 shadow-sm shrink-0">
                                <ShieldCheck className="h-3 w-3 mr-1 text-purple-600 flex-shrink-0" />
                                {currentProgramRecord.grandfatheredTier === 'Diamond'
                                  ? 'Lifetime Diamond'
                                  : currentProgramRecord.grandfatheredTier === 'Platinum'
                                  ? 'Grandfathered Platinum (+10k Boost)'
                                  : `Grandfathered ${currentProgramRecord.grandfatheredTier}`}
                              </span>
                            )}
                          </div>
                          {programName && (
                            <p className="text-xs font-medium text-gray-500">{programName}</p>
                          )}
                        </div>

                        <div className="flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => {
                              const rec = loyaltyStatus[index];
                              if (rec && isCarnivalCruiseLine(rec.cruiseLineName) && rec.grandfatheredTier === 'Platinum') {
                                const curVal = Number(rec.status) || 0;
                                if (curVal < CARNIVAL_PLATINUM_BOOST_STARS) {
                                  loyaltyForm.setValue(`loyaltyPrograms.${index}.status`, CARNIVAL_PLATINUM_BOOST_STARS);
                                }
                              }
                              setEditingLoyaltyIndex(index);
                            }}
                            className="flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            title="Edit points or loyalty number"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1 text-gray-500" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveLoyaltyProgram(index)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
                            title="Remove program"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                            Status ({loyaltyUnit})
                          </span>
                          <span className="text-sm font-semibold text-gray-900 mt-0.5 block">
                            {(isCarnivalCruiseLine(selectedCruiseLine) && currentProgramRecord?.grandfatheredTier === 'Platinum' && (loyaltyUnit?.toLowerCase().includes('star') || (currentStatus || 0) < CARNIVAL_PLATINUM_BOOST_STARS)
                              ? Math.max(CARNIVAL_PLATINUM_BOOST_STARS, currentStatus || 0).toLocaleString()
                              : (currentStatus !== undefined ? currentStatus.toLocaleString() : '0'))} {loyaltyUnit}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                            Loyalty Number
                          </span>
                          <span className="text-sm font-medium text-gray-700 mt-0.5 block">
                            {currentLoyaltyNumber ? `#${currentLoyaltyNumber}` : <span className="text-gray-400 italic font-normal">Not provided</span>}
                          </span>
                        </div>
                      </div>

                      {(currentProgramRecord?.legacyPoints !== undefined || currentProgramRecord?.cycleEndDate) && (
                        <div className="mt-3.5 bg-indigo-50/70 border border-indigo-100/90 rounded-lg p-2.5 sm:p-3 text-xs shadow-2xs">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 sm:divide-x sm:divide-indigo-100">
                            {currentProgramRecord?.legacyPoints !== undefined && (
                              <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3">
                                <span className="flex items-center gap-1.5 font-medium text-indigo-900">
                                  <History className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                                  Preserved VIFP:
                                </span>
                                <span className="font-semibold text-indigo-950 bg-white/90 border border-indigo-100/80 px-2 py-0.5 rounded text-xs shadow-2xs">
                                  {currentProgramRecord.legacyPoints} Sailing Days
                                </span>
                              </div>
                            )}
                            {currentProgramRecord?.cycleEndDate && (
                              <div className={`flex items-center justify-between sm:justify-start gap-2 sm:gap-3 ${currentProgramRecord?.legacyPoints !== undefined ? 'pt-2 sm:pt-0 border-t sm:border-t-0 sm:pl-4 border-indigo-100/80' : ''}`}>
                                <span className="flex items-center gap-1.5 font-medium text-indigo-900">
                                  <Calendar className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                                  Cycle Ends:
                                </span>
                                <span className="font-semibold text-indigo-950 bg-white/90 border border-indigo-100/80 px-2 py-0.5 rounded text-xs shadow-2xs">
                                  {formatCycleDate(currentProgramRecord.cycleEndDate)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {secondarySailingCount !== undefined && (
                        <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-md p-2 flex items-center justify-between border border-gray-100">
                          <span>{secondarySailingCount} completed sailing{secondarySailingCount !== 1 ? 's' : ''} (in-app)</span>
                          {secondaryWins && (
                            <span className="text-amber-700 font-medium">Cruise count wins</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </form>
        </motion.div>

        {/* Display Preferences Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center mb-4">
            <Settings className="h-5 w-5 text-secondary-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Display Preferences</h2>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Customize how dates, times, currency, and measurements are displayed throughout the app.
            These settings only affect how information is shown to you.
          </p>

          <form onSubmit={displayPreferencesForm.handleSubmit(onSubmitDisplayPreferences)} className="space-y-6">
            {/* Time Format */}
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Time Format
              </span>
              <div className="flex flex-wrap gap-4">
                {TIME_FORMAT_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value={option.value}
                      {...displayPreferencesForm.register('timeFormat')}
                      className="w-4 h-4 text-secondary-600 border-gray-300 focus:ring-secondary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {option.label} <span className="text-gray-400">({option.example})</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date Format */}
            <div>
              <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 mb-2">
                Date Format
              </label>
              <select
                id="dateFormat"
                {...displayPreferencesForm.register('dateFormat')}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
              >
                {DATE_FORMAT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} (e.g., {option.example})
                  </option>
                ))}
              </select>
            </div>

            {/* Currency */}
            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                Currency Display
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Note: This changes the currency symbol shown, but does not convert actual amounts.
              </p>
              <select
                id="currency"
                {...displayPreferencesForm.register('currency')}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
              >
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} - {currency.name} ({currency.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Units */}
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Units
              </span>
              <p className="text-xs text-gray-500 mb-2">
                Affects ship dimensions and other measurements.
              </p>
              <div className="flex flex-wrap gap-4">
                {UNIT_SYSTEM_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value={option.value}
                      {...displayPreferencesForm.register('units')}
                      className="w-4 h-4 text-secondary-600 border-gray-300 focus:ring-secondary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Week Starts On */}
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Week Starts On
              </span>
              <p className="text-xs text-gray-500 mb-2">
                Affects calendar displays throughout the app.
              </p>
              <div className="flex flex-wrap gap-4">
                {WEEK_START_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value={option.value}
                      {...displayPreferencesForm.register('weekStartsOn')}
                      className="w-4 h-4 text-secondary-600 border-gray-300 focus:ring-secondary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isLoadingDisplayPreferences}
                className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                {isLoadingDisplayPreferences ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Updating preferences...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Save Preferences
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>


        {/* Data Management Section */}
        {SHOW_DATA_MANAGEMENT && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex items-center mb-4">
              <Database className="h-5 w-5 text-secondary-500 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Data Management</h2>
            </div>

            {Capacitor.isNativePlatform() ? (
              /* Mobile App - Show message directing to web */
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <div className="flex items-start">
                  <Smartphone className="h-6 w-6 text-amber-500 mt-0.5 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-900 mb-2">Mobile App Limitation</h3>
                    <p className="text-amber-700 text-sm mb-4">
                      Data management features (import/export) are only available through our web experience for the best user experience with file handling.
                    </p>
                    <p className="text-amber-700 text-sm mb-4">
                      Please visit <strong>https://keep.cruisingalong.com</strong> on your desktop to access these features.
                    </p>
                    <a
                      href="https://keep.cruisingalong.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition duration-200 font-medium"
                    >
                      Open Web Version
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              /* Web Platform - Show full data management features */
              <>
                <p className="text-sm text-gray-500 mb-6">
                  Export your cruise data for backup or import cruises from a CSV file.
                </p>

                {/* Export Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Export All Cruises</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Download all your cruise data as a CSV file. This includes all cruise details, ports, and travel arrangements.
                  </p>
                  <button
                    onClick={handleExportCruises}
                    disabled={isExporting}
                    className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
                  >
                    {isExporting ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Download className="h-5 w-5 mr-2" />
                    )}
                    {isExporting ? 'Exporting...' : 'Export All Cruises'}
                  </button>
                </div>

                {/* Import Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Import Cruises</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Import cruise data from a CSV file. Only <strong>cruiseLine</strong>, <strong>shipName</strong>, <strong>departureDate</strong>, and <strong>returnDate</strong> are required.
                    All other columns are optional and will use default values if missing.
                  </p>

                  <div className="space-y-4">
                    {/* Template Download */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <FileText className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="font-medium text-blue-900 mb-1">Need a template?</h4>
                          <p className="text-blue-700 text-sm mb-3">
                            Download our CSV template to see the required format and column headers.
                          </p>
                          <button
                            onClick={handleDownloadTemplate}
                            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500 font-medium transition-colors"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download Template
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* File Selection */}
                    <div>
                      <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700 mb-2">
                        Select CSV File
                      </label>
                      <input
                        ref={fileInputRef}
                        id="csvFile"
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-secondary-50 file:text-secondary-700 hover:file:bg-secondary-100"
                      />
                    </div>

                    {/* Import Button */}
                    <button
                      onClick={handleImportCruises}
                      disabled={!selectedFile || isImporting}
                      className="flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {isImporting ? (
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-5 w-5 mr-2" />
                      )}
                      {isImporting ? 'Processing...' : 'Import Cruises'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Data Cleanup Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center mb-4">
            <Wrench className="h-5 w-5 text-orange-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Data Cleanup</h2>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Clean up port name variations to consolidate your cruise data. If you have different spellings or variations of the same port, this tool will help you merge them.
          </p>

          <button
            onClick={() => navigate('/port-cleanup')}
            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition duration-200 font-medium"
          >
            <Wrench className="h-5 w-5 mr-2" />
            Clean Up Port Names
          </button>
        </motion.div>

        {/* Delete Account Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500"
        >
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">Danger Zone</h2>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <p className="text-sm text-red-700">
              <strong>Warning:</strong> Deleting your account is permanent and cannot be undone. 
              All your data, including saved cruises, preferences, and loyalty information will be permanently removed.
            </p>
          </div>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 font-medium"
          >
            <Trash2 className="h-5 w-5 mr-2" />
            Delete Account
          </button>
        </motion.div>
      </div>

      {/* Delete Account Confirmation Modal - Using Portal */}
      {showDeleteModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-800">Delete Account</h2>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  deleteForm.reset();
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-gray-600">
                Are you absolutely sure you want to delete your account? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700">
                  <strong>This will permanently delete:</strong>
                </p>
                <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                  <li>Your account and profile information</li>
                  <li>All saved cruises and preferences</li>
                  <li>Loyalty program information</li>
                  <li>All associated data</li>
                </ul>
              </div>

            <form onSubmit={deleteForm.handleSubmit(onSubmitDelete)} className="space-y-4">
              {currentUser?.providerData.some(p => p.providerId === 'password') ? (
                <div>
                  <label htmlFor="deleteCurrentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Enter your current password to confirm
                  </label>
                  <PasswordInput
                    id="deleteCurrentPassword"
                    register={deleteForm.register('currentPassword', { required: 'Password is required to delete account' })}
                    placeholder="Current password"
                    error={!!deleteForm.formState.errors.currentPassword}
                    focusColor="red"
                  />
                  {deleteForm.formState.errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {deleteForm.formState.errors.currentPassword.message}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <p className="text-sm text-gray-700">
                    You are signed in using a social account (Google / Apple). No password is required to confirm deletion.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    deleteForm.reset();
                  }}
                  className="flex-1 flex items-center justify-center px-4 py-2 min-h-[42px] border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoadingDelete}
                  className="flex-1 flex items-center justify-center px-4 py-2 min-h-[42px] bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingDelete ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin flex-shrink-0" />
                      <span>Deleting...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Trash2 className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>Delete Account</span>
                    </span>
                  )}
                </button>
              </div>
            </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Account Linking Conflict Modal - Using Portal */}
      {linkingConflictProvider && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center mb-4 text-amber-600">
              <AlertTriangle className="h-6 w-6 mr-2 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900">{linkingConflictProvider} Account Already Linked</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              This {linkingConflictProvider} account is already attached to a secondary Cruising Along profile (created automatically when signing in via {linkingConflictProvider}).
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3.5 mb-5 text-xs text-amber-900 space-y-2">
              <p className="font-semibold text-amber-900">To connect your {linkingConflictProvider} ID to this account:</p>
              <ol className="list-decimal ml-4 space-y-1 text-amber-800">
                <li>Click <strong>Switch Account</strong> below to log out.</li>
                <li>Sign in with {linkingConflictProvider} to access that empty account.</li>
                <li>Go to Profile Settings ➔ Danger Zone and delete that secondary account.</li>
                <li>Sign back into this account and click <strong>Connect</strong>!</li>
              </ol>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setLinkingConflictProvider(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={async () => {
                  setLinkingConflictProvider(null);
                  try {
                    await logout();
                  } finally {
                    navigate('/login', { replace: true });
                  }
                }}
                className="px-4 py-2 bg-secondary-600 text-white rounded-md text-sm font-medium hover:bg-secondary-500 transition-colors"
              >
                Switch Account
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Import Confirmation Modal - Using Portal */}
      {showImportModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Confirm Import</h2>
              <button
                onClick={cancelImport}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <p className="text-gray-600 mb-4">
                Ready to import <strong>{importPreview.length}</strong> cruise{importPreview.length !== 1 ? 's' : ''}? 
                Please review the preview below to ensure the data looks correct.
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <p className="text-sm text-yellow-700">
                  <strong>Note:</strong> This will add new cruises to your account. Existing cruises will not be modified.
                </p>
              </div>

              {/* Preview of first few cruises */}
              <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                <h4 className="font-medium text-gray-800 mb-3">Preview (first 3 cruises):</h4>
                <div className="space-y-3">
                  {importPreview.slice(0, 3).map((cruise, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Ship:</span> {cruise.shipName}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Cruise Line:</span> {cruise.cruiseLine}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Departure:</span> {cruise.departureDate}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Return:</span> {cruise.returnDate}
                        </div>
                      </div>
                    </div>
                  ))}
                  {importPreview.length > 3 && (
                    <div className="text-center text-sm text-gray-500">
                      ... and {importPreview.length - 3} more cruise{importPreview.length - 3 !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
              <button
                onClick={cancelImport}
                disabled={isImporting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmImport}
                disabled={isImporting}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isImporting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Upload className="h-5 w-5 mr-2" />
                )}
                {isImporting ? 'Importing...' : `Import ${importPreview.length} Cruise${importPreview.length !== 1 ? 's' : ''}`}
              </button>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Edit Friend Username Modal */}
      {showEditFriendUsernameModal && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !isLoadingEditFriendUsername && setShowEditFriendUsernameModal(false)}
        >
          <motion.div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Edit friend username</h2>
              <button
                type="button"
                onClick={() => !isLoadingEditFriendUsername && setShowEditFriendUsernameModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveFriendUsername} className="p-6 space-y-4">
              <div>
                <label htmlFor="editFriendUsername" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <div className="flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 font-medium sm:text-sm select-none">
                    @
                  </span>
                  <input
                    id="editFriendUsername"
                    type="text"
                    value={editFriendUsernameValue}
                    onChange={(e) => setEditFriendUsernameValue(e.target.value.replace(/^@+/, ''))}
                    placeholder="cruise_fan_2024"
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 text-sm"
                    disabled={isLoadingEditFriendUsername}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  6–20 characters. Letters, numbers, and underscores only. Your handle will be @{editFriendUsernameValue || 'username'}.
                </p>
              </div>
              {editFriendUsernameError && (
                <p className="text-sm text-red-600">{editFriendUsernameError}</p>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditFriendUsernameModal(false)}
                  disabled={isLoadingEditFriendUsername}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoadingEditFriendUsername}
                  className="flex-1 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingEditFriendUsername ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default UserProfile;